import * as React from 'react';
import allytm from '@ally/allytm';
import { deprecated, muiInfoFill } from '@ally/metronome-icons';
import { Box, Icon, Space, TextBody } from '@ally/metronome-ui';
import { MOBILE_APPROVE_DENIED, MOBILE_APPROVE_EXPIRED, } from '@ally/transmitigator';
import { CallUsModal } from '../../../../Modals';
import { stringifyError } from '../../../../Utils';
import log from '../../../../whisper';
import { ErrorBanner } from '../../../ErrorBanner';
const EnrollCreditCardSuccessBanner = ({ error, }) => {
    const errorMessage = 'Heads up - it may be up to a day before you can manage your new account online.';
    React.useEffect(() => {
        allytm.event('customError', errorMessage, stringifyError(error));
    }, []);
    return (React.createElement(Box, { display: "flex", alignItems: "center", mb: "md", "data-allytm-error": errorMessage },
        React.createElement(Box, { flexShrink: 0 },
            React.createElement(Icon, { icon: muiInfoFill, fill: "plum", mr: "xs", size: "lg" })),
        React.createElement(TextBody, { size: "sm", tag: "p", focusOnMount: true }, errorMessage)));
};
export const EnrollCreditCardFailureBanner = ({ error, }) => {
    const errorMessage = 'We need a little more time on our end — come back in 24 hours and choose Create a profile to set up online access for your card.';
    React.useEffect(() => {
        allytm.event('customError', errorMessage, stringifyError(error));
    }, []);
    return (React.createElement(Box, { display: "flex", alignItems: "center", mb: "md", "data-allytm-error": errorMessage },
        React.createElement(Box, { flexShrink: 0 },
            React.createElement(Icon, { icon: deprecated.muiErrorFill, fill: "error", mr: "xs", size: "lg" })),
        React.createElement(TextBody, { size: "sm", tag: "p", focusOnMount: true },
            "We need a little more time on our end \u2014 come back in 24 hours and choose",
            ' ',
            React.createElement(TextBody, { size: "sm", tag: "b", weight: "bold" }, "Create a profile"),
            ' ',
            "to set up online access for your card.")));
};
export const InvalidCredentialsBanner = ({ error, }) => {
    const errorMessage = "The username and password you entered aren't valid. Try again (and keep in mind that we'll lock your account after 5 failed attempts).";
    React.useEffect(() => {
        allytm.event('customError', errorMessage, stringifyError(error));
    }, []);
    return (React.createElement(Box, { display: "flex", mb: "md" },
        React.createElement(Box, { flexShrink: 0 },
            React.createElement(Icon, { icon: deprecated.muiErrorFill, fill: "error", mr: "xs", size: "lg" })),
        React.createElement(TextBody, { tabIndex: -1, size: "sm", tag: "b", focusOnMount: true },
            React.createElement(Space, null,
                React.createElement(TextBody, { tag: "p", size: "sm" }, "The username and password you entered aren't valid.")),
            "Try again (and keep in mind that we'll lock your account after 5 failed attempts).")));
};
export const TempPasswordLinkExpiredFailureBanner = ({ error, shouldShowAllLOBs = true, }) => {
    const errorMessage = 'Your password reset link expired. Give us a call to access your account.';
    React.useEffect(() => {
        allytm.event('customError', errorMessage, stringifyError(error));
    }, []);
    return (React.createElement(Box, { display: "flex", alignItems: "center", mb: "md", "data-allytm-error": errorMessage },
        React.createElement(Box, { flexShrink: 0 },
            React.createElement(Icon, { icon: deprecated.muiErrorFill, fill: "error", mr: "xs", size: "lg" })),
        React.createElement(TextBody, { size: "sm", tag: "p", focusOnMount: true },
            "Your password reset link expired.",
            ' ',
            React.createElement(TextBody, { size: "sm", tag: "b", weight: "bold" },
                React.createElement(CallUsModal, { triggerContent: "Give us a call", shouldShowAllLOBs: shouldShowAllLOBs })),
            ' ',
            "to access your account.")));
};
const LoginErrorBanner = ({ error, mb = 'sm', className, }) => {
    let errorMessage = "Something's not working. Try again later.";
    // If you return a customErrorComponent it will render instead of the
    // default ErrorBanner. You will be responsible for handling any
    // necessary analytics within the custom component
    let customErrorComponent;
    switch (error) {
        case 'CIAM_E08_07_001':
        case 'CIAM_E08_07_002':
            errorMessage = "Hmm. We're having trouble finding you. Try again.";
            break;
        case 'CIAM_E08_02_006':
            // This is a custom error banner and will handle the analytics call in the component
            customErrorComponent = React.createElement(EnrollCreditCardSuccessBanner, { error: error });
            break;
        case 'CIAM_E08_03_004':
        case 'CIAM_E08_03_003':
            // This is a custom error banner and will handle the analytics call in the component
            customErrorComponent = React.createElement(EnrollCreditCardFailureBanner, { error: error });
            break;
        case 'CIAM_E00_00_014':
            // This is a custom error banner and will handle the analytics call in the component
            customErrorComponent = (React.createElement(TempPasswordLinkExpiredFailureBanner, { error: error }));
            break;
        case 'CIAM_E08_03_005':
            errorMessage =
                'Something went wrong, but we saved your new username and password. Log in later to finish enrolling.';
            break;
        case 'CIAM_E08_03_006':
            errorMessage = 'Something went wrong — try logging in again later.';
            break;
        case 'CIAM_E01_01_001':
            // This is a custom error banner and will handle the analytics call in the component
            customErrorComponent = React.createElement(InvalidCredentialsBanner, { error: error });
            break;
        case 'CIAM_E01_07_003':
            errorMessage =
                "Your Ally Auto login isn't the same as an Ally login. Select \"You don't have an Ally login\" to open your first bank account with us. In the future, you'll have one login for all your accounts.";
            break;
        case 'CIAM_E01_01_016':
            errorMessage =
                "It's not you, it's (probably) your password manager. Try logging in again, and check that your full username is saved, without dots or other invalid special characters.";
            break;
        case MOBILE_APPROVE_DENIED:
            errorMessage =
                "You didn't approve this login. If that was a mistake, try logging in again. We'll lock your account after too many attempts.";
            break;
        case MOBILE_APPROVE_EXPIRED:
            errorMessage =
                'The login notification we sent timed out before you responded. Try logging in again and be sure to open the notification right when you get it.';
            break;
        default:
            if (error) {
                log.error({
                    message: [`Unknown error code returned from transmit: ${error}`],
                });
            }
    }
    React.useEffect(() => {
        if (error && !customErrorComponent) {
            allytm.event('customError', errorMessage, stringifyError(error));
        }
    }, []);
    if (!error) {
        return null;
    }
    return (React.createElement(Box, { className: className }, customErrorComponent || (React.createElement(ErrorBanner, { mb: mb }, errorMessage))));
};
export default LoginErrorBanner;
//# sourceMappingURL=LoginErrorBanner.js.map